import { FormControl, FormHelperText, InputAdornment, OutlinedInput, styled } from "@mui/material";
import React, { useMemo } from "react";
import { type Control, Controller, type FieldError, type FieldValues } from "react-hook-form";

import { ariaLabel } from "src/constants";
import { amountInCents, formatAmountInput } from "src/utils";

import { EuroEndAdornment } from "../EuroEndAdornment";

const StyledOutlinedInput = styled(OutlinedInput)`
  background-color: ${(props) => props.theme.palette.common.white};
`;

const MESSAGE_MIN_AMOUNT_CONTROL = "Le montant à prélever ne peut être inférieur à 1 €";
const MESSAGE_MAX_AMOUNT_CONTROL = "Le montant à prélever ne peut être supérieur au reste à payer";

type Props<T extends FieldValues> = {
  projectedBalance: number;
  error: FieldError | undefined;
  control: Control<T>;
  name?: string;
  messageMinAmountControl?: string;
  messageMaxAmountControl?: string;
};

export const AmountFormControl = <TFieldValues extends FieldValues>({
  projectedBalance,
  error,
  control,
  name = "amount",
  messageMinAmountControl = MESSAGE_MIN_AMOUNT_CONTROL,
  messageMaxAmountControl = MESSAGE_MAX_AMOUNT_CONTROL,
}: Props<TFieldValues>) => {
  const rules = useMemo(
    () => ({
      required: "Un montant est requis",
      validate: (value: any) => {
        const amountValue = amountInCents(value);

        if (amountValue < 100) {
          // < 1€
          return messageMinAmountControl;
        }
        if (amountValue > Math.abs(projectedBalance)) {
          return messageMaxAmountControl;
        }
        return true;
      },
    }),
    [projectedBalance]
  );
  return (
    <FormControl fullWidth variant="outlined" error={Boolean(error)}>
      <Controller
        // @ts-expect-error: Unreachable code error
        name={name}
        control={control}
        rules={rules}
        render={({ field: { value, onChange } }) => (
          <StyledOutlinedInput
            name={name}
            value={value}
            onChange={(event) => {
              onChange(formatAmountInput(event.target.value));
            }}
            inputProps={{
              "aria-label": ariaLabel.amountToWithdrawInput,
            }}
            endAdornment={
              <InputAdornment position="end">
                <EuroEndAdornment error={Boolean(error)} />
              </InputAdornment>
            }
          />
        )}
      />
      <FormHelperText>{error?.message}</FormHelperText>
    </FormControl>
  );
};
